/**
 * Pure logic for the Split Product operation — the division-suffix
 * algorithm and LIMS-total scaling. Kept dependency-free (no React/network)
 * so it can be unit-tested; see `productSplit.test.ts`.
 */

const UPPER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
const LOWER = 'abcdefghijklmnopqrstuvwxyz'

/**
 * `count` sequential letters from `alphabet`, starting at `startLetter`
 * (inclusive) and wrapping past the end back to the start — wrapping is a
 * defensive fallback for pathologically wide splits, not an expected path.
 */
function sequentialLettersFrom(startLetter: string, count: number, alphabet: string): string[] {
  const startIdx = alphabet.indexOf(startLetter)
  return Array.from({ length: count }, (_, i) => alphabet[(startIdx + i) % alphabet.length])
}

/**
 * Division suffixes for `count` new aliquots split off a product whose own
 * accumulated suffix (its `productDescriptionCode` minus the catalog's base
 * code) is `currentSuffix` — `''` for a product that has never itself been
 * split.
 *
 * Every lineage keeps reusing the SAME letter it was first assigned at the
 * very first split, alternating case one generation at a time — the first
 * child of any further split continues that letter into the next case; a
 * second, third, … sibling at that same split gets the next letters in
 * sequence after it. So a single-child chain off branch "B" reads B0 → Bb →
 * BbB → BbBb (always "B"/"b"), while a multi-child split of "A0" reads Aa,
 * Ab, Ac… (sequence starting at the "A" branch's own letter, "a").
 *
 * - First split of an unsplit product: each child gets its own uppercase
 *   letter plus "0" — A0, B0, C0…
 * - Splitting a child that hasn't been split further yet (suffix ends in
 *   "0"): the "0" is replaced by a lowercase letter, starting from the
 *   branch's own letter — A0 → Aa, Ab, Ac…
 * - Splitting anything deeper appends one more character, alternating case
 *   and starting the sequence from the branch's own letter re-cased — Aa →
 *   AaA, AaB…; AaA → AaAa, AaAb… — so depth is unlimited, not capped at any
 *   fixed generation count.
 */
export function nextDivisionSuffixes(currentSuffix: string, count: number): string[] {
  if (count < 1) return []
  if (count > UPPER.length) {
    throw new Error(`Cannot split into more than ${UPPER.length} aliquots in one operation.`)
  }
  if (currentSuffix === '') {
    return sequentialLettersFrom('A', count, UPPER).map(l => `${l}0`)
  }
  const rootLetter = currentSuffix[0].toUpperCase()
  if (currentSuffix.endsWith('0')) {
    const base = currentSuffix.slice(0, -1)
    return sequentialLettersFrom(rootLetter.toLowerCase(), count, LOWER).map(l => `${base}${l}`)
  }
  const lastChar = currentSuffix[currentSuffix.length - 1]
  const nextAlphabet = lastChar === lastChar.toUpperCase() ? LOWER : UPPER
  const startLetter = nextAlphabet === UPPER ? rootLetter : rootLetter.toLowerCase()
  return sequentialLettersFrom(startLetter, count, nextAlphabet).map(l => `${currentSuffix}${l}`)
}

/** The accumulated division suffix of a product, given its catalog entry's base code. */
export function divisionSuffixOf(productDescriptionCode: string, baseCode: string): string {
  return productDescriptionCode.startsWith(baseCode)
    ? productDescriptionCode.slice(baseCode.length)
    : ''
}

// ─── Volume ─────────────────────────────────────────────────────────────────

/**
 * Split `totalMl` evenly across `count` children (2dp). The last child
 * absorbs the rounding remainder so the parts always sum back to the total.
 */
export function evenVolumeSplit(totalMl: number, count: number): number[] {
  if (count < 1) return []
  const each = Math.round((totalMl / count) * 100) / 100
  const amounts = Array.from({ length: count }, () => each)
  const roundedTotal = each * count
  amounts[count - 1] = Math.round((amounts[count - 1] + (totalMl - roundedTotal)) * 100) / 100
  return amounts
}
